import React, { useState, useEffect, useCallback } from 'react';
import { Navigate } from "react-router-dom";

import Grid from '@mui/material/Grid2';

import { FeatureSource, ModelBase } from "@models";
import { Creature, Spell, TemplateBase } from "@dndModels";
import { DATA_TYPE_INFO } from "@dndModels/Constants";

import CommonUtilities from "@utilities/common_utilities";
// import DandDAPI from '../utilities/system_api';

import StringBox from "@components/input/strings/StringBox";
import ModelBaseDetails from "@dndComponents/ModelBaseDetails";
import TemplateBox from "@dndComponents/TemplateBox";
import LoadingBox from "@components/input/LoadingBox";
import ArrowBackButton from "@components/input/buttons/ArrowBackButton";
import CenteredMenu from "@components/input/menus/CenteredMenu";

interface Props { 
  obj: ModelBase;
  disabled: boolean;
  onChange: (obj: ModelBase) => void;
  change_time?: number;
  no_source?: boolean;
  image_options?: string[];
  header?: React.ReactNode;
  children?: React.ReactNode;
  extra_menu_options?: { [menu_option: string]: React.ReactNode };
  onTabChange?: ((changed: string) => void) | null;
  no_back_button?: boolean;
  submitControls?: React.ReactNode;
  onSubmit?: (changed: ModelBase) => void;
  onCancel?: () => void;
  processing?: boolean;
}

// const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function ModelBaseInput({
  obj,
  disabled,
  onChange,
  change_time = -1,
  no_source = false,
  image_options,
  header,
  children,
  extra_menu_options = {},
  onTabChange,
  no_back_button = false,
  submitControls,
  onSubmit,
  onCancel,
  processing
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [myObj, setObj] = useState(obj.clone());
  const [my_change_time, setChangeTime] = useState(-1);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [tab, setTab] = useState("Description");

  useEffect(() => {
    if (obj._id !== myObj._id) {
      setObj(obj.clone());
      setChangeTime(common_util.get_time_number());
    }
    // eslint-disable-next-line
  }, [obj]);

  const load = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
    }, 500);
  }, []);
  
  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      load();
    }
  }, [fetched, load]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  useEffect(() => {
    if (change_time !== my_change_time) {
      setObj(obj.clone());
      setChangeTime(change_time);
    }
    // eslint-disable-next-line
  }, [change_time]);

  const renderTab = () => {
    let content: any = null;
    if (tab === "Description") {
      content = (
        <Grid
          size={{
            xs: 12
          }}>
          <ModelBaseDetails
            obj={myObj}
            disabled={disabled}
            change_time={my_change_time}
            image_options={image_options}
            topHeight={128}
            onChange={(changed: ModelBase) => {
              const new_obj = myObj;
              new_obj.copy(changed);
              setObj(new_obj);
              onChange(new_obj);
            }}
            children={children}
            submitControls={submitControls}
            onSubmit={onSubmit}
            onCancel={onCancel}
            processing={processing}
          />
        </Grid>
      );
    } else if (Object.keys(extra_menu_options).includes(tab)) {
      content = extra_menu_options[tab];
    }
    return (
      <Grid container spacing={0} direction="row">
        { content }
      </Grid>
    );
  }

  const renderHeader = () => {
    if (header) {
      return (
        <Grid
          size={{
            xs: 12
          }}>
          { header }
        </Grid>
      );
    }
    return null;
  }

  const changeTab = (newTab: string) => {
    setTab(newTab);
    if (onTabChange) {
      onTabChange(newTab);
    }
  }

  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (!fetched || loading) {
    return (<LoadingBox />);
  } else { 
    const dt_info = DATA_TYPE_INFO.find(o => o.data_type === obj.data_type);
    return (
      <Grid container spacing={0} direction="row" className="padded">
        <Grid
          size={{
            xs: 12
          }}>
          <Grid container spacing={0} direction="row">
            <Grid
              size={{
                xs: 3
              }}>
              { !no_back_button &&
                <ArrowBackButton
                  onClick={ () => {
                    setNavigateTo(`/dandd/${obj.data_type}`);
                  }}
                />
              }
            </Grid>
            { !disabled && (obj instanceof FeatureSource || obj instanceof Creature || obj instanceof Spell) &&
              <Grid
                size={{
                  xs: 9
                }}>
                <TemplateBox
                  obj={obj}
                  type={dt_info?.name??""}
                  useTemplate={(template: TemplateBase) => {
                    const new_obj = obj;
                    if (new_obj instanceof FeatureSource || new_obj instanceof Creature || new_obj instanceof Spell) {
                      new_obj.copyTemplate(template);
                    }
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    onChange(new_obj);
                  }}
                />
              </Grid>
            }
            <Grid
              size={{
                xs: 12
              }}>
              <span className="header">
                { obj._id === "" ? `Create ${dt_info?.name??""}` : `Edit ${obj.name}` }
              </span>
            </Grid>
            { renderHeader() }
            <Grid
              size={{
                xs: 12
              }}>
              { disabled ? <><span className="header">Name:</span> {myObj.name}</> : 
                <StringBox 
                  value={myObj.name} 
                  message={myObj.name.length > 0 ? "" : "Name Invalid"}
                  name="Name"
                  onBlur={(value: string) => {
                    const new_obj = myObj;
                    new_obj.name = value;
                    setObj(new_obj);
                    setChangeTime(common_util.get_time_number());
                    onChange(myObj);
                  }}
                />
              }
            </Grid>
            { Object.keys(extra_menu_options).length > 0 && 
              <Grid
                style={{ height: "23px" }}
                size={{
                  xs: 12
                }}>
                <CenteredMenu 
                  options={["Description",...Object.keys(extra_menu_options)]}
                  selected={tab}
                  onSelect={changeTab}
                />
              </Grid>
            }
          </Grid>
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { renderTab() }
        </Grid>
      </Grid>
    ); 
  }
}